<template>
<div class="page-config">
    <div class="config-preview">
        <PageViewer class="box"/>
    </div>
    <transition name="fade" mode="out-in">
        <div v-if="isLoadingChannel" class="loading-section" key="load">
            <Loader size="md" overlay/>
        </div>
        <div v-else>
            <OAuthSetup :requiresOAuthSetup="requiresOAuthSetup"/>
            <form
                @submit.prevent="onSubmit"
                ref="form"
                id="config-form">
                <h3 class="subtitle is-3">Settings</h3>
                <ConfigHeaderType :useTextHeader.sync="settings.useTextHeader" />
                <ConfigHeaderText
                    v-if="settings.useTextHeader"
                    :title.sync="settings.title"
                    :titleFontSize.sync="settings.titleFontSize"
                    :titleFontUrl.sync="settings.titleFontUrl"
                    :titleGradient.sync="settings.titleGradient"/>
                <ConfigHeaderImage
                    v-else
                    :headerImgUrl.sync="settings.headerImgUrl"/>
                <ConfigMessageToViewers v-model.trim="settings.messageToViewer"/>
                <Theme :theme.sync="settings.theme"/>
                <ConfigFont :fontUrl.sync="settings.fontUrl"/>
                <ConfigListType v-model="settings.listType"/>
                <PostCooldown v-model="settings.postCooldownMinutes"/>
                <PostLength v-model="settings.suggestionMaxLength"/>
                <PostModeration v-bind.sync="settings"/>
                <ConfigEmail v-model.trim="settings.email" />
                <Tips/>
                <ContactSection/>
                <div class="save-config">
                    <button class="button" :class="saveBtnClass">
                        {{ saveBtnText }}
                    </button>
                    <span v-show="saveErrMsg" class="help has-text-danger">
                        {{ saveErrMsg }}
                    </span>
                </div>
            </form>
        </div>
    </transition>
</div>
</template>

<script>
/*
The embedded Suggestion Box reacts to the config page settings b/c they both
use the same vuex store.
*/
import { mapState, mapGetters } from 'vuex'

import * as userUtil from '@shared/user-util'
import * as _ from '@/lodash'
import PageViewer from '@/views/PageViewer'
import OAuthSetup from '@/components/config/OAuthSetup'
import ConfigHeaderType from '@/components/config/ConfigHeaderType'
import ConfigHeaderText from '@/components/config/ConfigHeaderText'
import ConfigHeaderImage from '@/components/config/ConfigHeaderImage'
import ConfigFont from '@/font-loader/ConfigFont'
import PostCooldown from '@/components/config/PostCooldown'
import PostLength from '@/components/config/PostLength'
import ConfigMessageToViewers from '@/components/config/ConfigMessageToViewers'
import Theme from '@/components/config/Theme'
import ConfigListType from '@/components/config/ConfigListType'
import ConfigEmail from '@/components/config/ConfigEmail'
import PostModeration from '@/components/config/PostModeration'
import Tips from '@/components/config/Tips'
import ContactSection from '@/components/config/ContactSection'
import * as C from '@shared/constants'
import * as M from '@/store/mutations'
import { sleep } from '@shared/utils'

const defaultSettings = userUtil.defaultChannelSettings()
const settingKeys = Object.keys(defaultSettings)

export default {
    name: 'PageConfig',
    data: () => ({
        settings: defaultSettings,
        isSavingSettings: false,
        saveErr: null,
    }),
    computed: {
        ...mapState(['channel', 'lastSavedChannel', 'headerImgLoadingState']),
        ...mapGetters(['isLoadingChannel', 'requiresOAuthSetup']),
        hasUnsavedChanges () {
            const originalSettings = _.pick(this.lastSavedChannel, settingKeys)
            const currentSettings = _.pick(this.channel, settingKeys)

            return !_.isEqual(originalSettings, currentSettings)
        },
        saveBtnClass (vm) {
            let btnClass
            if (vm.saveErr) {
                btnClass = 'is-danger'
            } else if (vm.hasUnsavedChanges) {
                btnClass = 'is-warning'
            } else {
                btnClass = 'is-success'
            }
            const isLoadingHeaderImg = vm.headerImgLoadingState === C.STATE_LOADING

            return [
                { 'is-loading': vm.isSavingSettings || isLoadingHeaderImg },
                btnClass
            ]
        },
        saveBtnText (vm) {
            if (vm.saveErr) {
                return 'Error'
            } else if (vm.hasUnsavedChanges) {
                return 'Save Changes'
            } else {
                return 'Save'
            }
        },
        saveErrMsg: vm => {
            const res = vm.saveErr?.response
            const msg = res?.data?.message

            if (msg) { return msg }
            if (res?.status) {
                return `Error status: ${res?.status}`
            }
            return null
        }
    },
    watch: {
        channel: {
            handler () {
                // BEWARE OF INFINITE UPDATE LOOP.
                this.settings = { ...this.channel }
            },
            deep: true
        },
        settings: {
            // throttle commits b/c color pickers mutate data very quickly
            handler: _.throttle(function () {
                const channel = { ...this.settings }
                this.$store.commit(M.UPDATE_CHANNEL, { channel })
            }, 50),
            deep: true
        }
    },
    created () {
        if (process.env.NODE_ENV !== 'production') { return }

        window.addEventListener('beforeunload', e => {
            if (this.hasUnsavedChanges) {
                const msg = 'You have unsaved changes';
                (e || window.event).returnValue = msg
                return msg
            }
        })
    },
    methods: {
        async onSubmit () {
            try {
                this.isSavingSettings = true
                this.saveErr = null

                await Promise.all([
                    this.$store.dispatch('updateChannel', { channel: this.settings }),
                    sleep(350)
                ])
            } catch (err) {
                this.saveErr = err
                throw err
            } finally {
                this.isSavingSettings = false
            }
        },
    },
    components: {
        PageViewer,
        OAuthSetup,
        ConfigHeaderType,
        ConfigHeaderImage,
        ConfigHeaderText,
        ConfigFont,
        PostCooldown,
        PostLength,
        PostModeration,
        ConfigEmail,
        ConfigMessageToViewers,
        Theme,
        ConfigListType,
        ContactSection,
        Tips,
    }
}
</script>

<style lang="scss" scoped>
$padding: 2rem;
.page-config {
    display: flex;
    //border: 1px solid black;
    height: 100%;
    padding: $padding;
    overflow: auto;
}

.config-preview {
    position: sticky;
    top: 0px;
    margin-right: 3rem;
    display: flex;
    flex-direction: column;
    .page-viewer.box {
        flex: 1 1 $sbox-panel-height;
        min-height: 300px;
        padding: 0; // bulma override
        border-radius: 0;
    }
}

#config-form,
.save-config {
    /deep/ .help {
        font-size: 0.85rem;
        color: $dark-theme-text-color-darker;
        margin-bottom: 0.2rem;
    }
}

#config-form > *:not(:last-child):not(.toggle-header-type) {
    margin-bottom: 2rem;
}

#config-form .save-config {
    position: sticky;
    bottom: -2rem;
    padding: 0.75rem 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 5;
    background: var(--bg-color);
    // border-top is buggy with position: sticky. Use a pseudo element to
    // emulate border-top.
    &:after{
        content:'';
        position:absolute;
        left: 0;
        top: 0;
        width: 100%;
        border-top: 1px solid rgba(130, 130, 130, 0.5);
    }
}

.loading-section {
    position: relative;
    flex: 1;
}
</style>
